import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { api, ifMatch, newIdempotencyKey, unwrap } from '@/api/client';
import { describeError, isApiError } from '@/api/errors';
import { useCommand } from '@/app/commands';
import { useOrgId } from '@/app/session';
import {
  Button,
  ErrorState,
  Field,
  LoadingState,
  Modal,
  TextArea,
  statusDef,
  useToast,
  type PipelineState,
} from '@/components/ui';
import {
  ACTIVE_STAGES,
  conversationsQuery,
  GUARDED_TARGETS,
  locationKeys,
  siteChangedKeys,
  type FeedParams,
  type Location,
  type LocationPage,
  type LocationSummary,
} from './api';

export interface LocationRef {
  id: string;
  version: number;
  name: string;
  pipeline_state: PipelineState;
}

const REASON_MAX = 2000;
const stageLabel = (s: PipelineState) => statusDef('pipeline', s).label;

/** Short reason for a failed command: the server's own detail when it gave one. */
function failure(e: unknown): string {
  return isApiError(e) && e.problem.detail ? e.problem.detail : describeError(e).title;
}

/** Exact effects of a guarded move, shown before the command is sent. */
function transitionEffects(target: PipelineState): string[] {
  switch (target) {
    case 'disqualified':
      return ['Hidden from the board unless Show closed is on', 'No mail sent'];
    case 'archived':
      return ['Hidden from the board unless Show closed is on', 'Data and history kept · Reversible', 'No mail sent'];
    case 'cancelled':
      return ['Queued work stops · Unsent drafts withdrawn', 'Mail already handed to the mail provider may still go out', 'Automation stays off after a move back'];
    case 'ready_for_serious_engagement':
      return ['Automatic mail stops for the site and the threads below', 'Queued AI work cancelled', 'Automation stays off after a move back'];
    default:
      return ['No mail sent'];
  }
}

function ReasonField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <Field label="Reason" required hint="Saved to the site history">
      <TextArea rows={3} maxLength={REASON_MAX} value={value} onChange={(e) => onChange(e.target.value)} />
    </Field>
  );
}

function Effects({ items }: { items: string[] }) {
  return (
    <ul className="loc-effects" aria-label="Effects">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

/** Open threads at the location: the ones a handoff or readiness decision stops. */
function AffectedConversations({ locationId }: { locationId: string }) {
  const orgId = useOrgId();
  const q = useQuery(conversationsQuery(orgId, locationId));
  if (q.isPending) return <LoadingState compact lines={2} label="Loading threads" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const open = q.data.items.filter((c) => c.state !== 'closed' && c.state !== 'archived');
  if (open.length === 0) return <p className="small muted">No open threads</p>;
  return (
    <div className="stack-sm">
      <h3 className="section-title">Threads affected</h3>
      <ul className="loc-effects">
        {open.map((c) => (
          <li key={c.id}>
            {c.title}
            {c.counterpart ? <span className="faint"> · {c.counterpart.name || c.counterpart.email}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Guarded stage move with a reason. Ready uses decideReadiness and Cancelled uses
 * cancelLocationWork; every other target uses transitionLocation. All are version guarded.
 */
export function TransitionDialog({
  location,
  target,
  onClose,
}: {
  location: LocationRef | null;
  target: PipelineState | null;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(newIdempotencyKey);
  const open = Boolean(location && target);
  const close = () => {
    setReason('');
    setKey(newIdempotencyKey());
    onClose();
  };
  const cmd = useCommand({
    run: async ({ loc, to, why }: { loc: LocationRef; to: PipelineState; why: string }) => {
      const path = { orgId, locationId: loc.id };
      const header = { 'If-Match': ifMatch(loc.version), 'Idempotency-Key': key };
      if (to === 'ready_for_serious_engagement') {
        return unwrap(
          api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/readiness', {
            params: { path, header },
            body: { decision: 'mark_ready', reason: why },
          }),
        );
      }
      if (to === 'cancelled') {
        return unwrap(api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/cancel', { params: { path, header }, body: { reason: why } }));
      }
      return unwrap(
        api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/transition', {
          params: { path, header },
          body: { target_state: to, reason: why },
        }),
      );
    },
    success: () => (target ? `Moved to ${stageLabel(target)} · No mail sent` : 'Moved · No mail sent'),
    invalidate: siteChangedKeys(orgId, location?.id),
    onSuccess: close,
  });
  if (!location || !target) return null;
  const trimmed = reason.trim();
  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!cmd.isPending}
      title={`Move to ${stageLabel(target)}`}
      description={`${location.name} · ${stageLabel(location.pipeline_state)}`}
      footer={
        <>
          <Button variant="quiet" onClick={close} disabled={cmd.isPending}>
            Cancel
          </Button>
          <Button
            variant={target === 'disqualified' || target === 'cancelled' ? 'danger' : 'primary'}
            loading={cmd.isPending}
            disabled={!trimmed}
            onClick={() => cmd.mutate({ loc: location, to: target, why: trimmed })}
          >
            Move to {stageLabel(target)}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Effects items={transitionEffects(target)} />
        {target === 'ready_for_serious_engagement' || target === 'cancelled' ? <AffectedConversations locationId={location.id} /> : null}
        <ReasonField value={reason} onChange={setReason} />
      </div>
    </Modal>
  );
}

/** handoffLocation: a person handles all mail for this location from now on. */
export function HandoffDialog({ location, open, onClose }: { location: LocationRef; open: boolean; onClose: () => void }) {
  const orgId = useOrgId();
  const [reason, setReason] = useState('');
  const [allowEnrichment, setAllowEnrichment] = useState(true);
  const [key, setKey] = useState(newIdempotencyKey);
  const close = () => {
    setReason('');
    setKey(newIdempotencyKey());
    onClose();
  };
  const cmd = useCommand({
    run: (why: string) =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/handoff', {
          params: { path: { orgId, locationId: location.id }, header: { 'If-Match': ifMatch(location.version), 'Idempotency-Key': key } },
          body: { reason: why, allow_enrichment: allowEnrichment, notify: true },
        }),
      ),
    success: (r) =>
      r.revoked_draft_ids.length ? `Handed off · AI drafts withdrawn ${r.revoked_draft_ids.length} · No mail sent` : 'Handed off · No mail sent',
    invalidate: siteChangedKeys(orgId, location.id),
    onSuccess: close,
  });
  const trimmed = reason.trim();
  return (
    <Modal
      open={open}
      onClose={close}
      dismissible={!cmd.isPending}
      title="Hand off to a person"
      description={location.name}
      footer={
        <>
          <Button variant="quiet" onClick={close} disabled={cmd.isPending}>
            Cancel
          </Button>
          <Button variant="primary" loading={cmd.isPending} disabled={!trimmed} onClick={() => cmd.mutate(trimmed)}>
            Hand off
          </Button>
        </>
      }
    >
      <div className="stack">
        <Effects
          items={[
            'All mail written by a person · AI drafting stops',
            'Queued AI work cancelled · Unsent AI drafts withdrawn',
            'Mail already handed to the mail provider may still go out',
          ]}
        />
        <AffectedConversations locationId={location.id} />
        <label className="checkbox">
          <input type="checkbox" checked={allowEnrichment} onChange={(e) => setAllowEnrichment(e.target.checked)} />
          <span>Keep researching facts</span>
        </label>
        <ReasonField value={reason} onChange={setReason} />
      </div>
    </Modal>
  );
}

// ---------- Loaded lanes and lists (infinite feeds) ----------

type Feed = InfiniteData<LocationPage, string | undefined>;

const feedParams = (key: QueryKey) => key[key.length - 1] as FeedParams;
/** Feeds that differ only by stage: the lanes of one board. */
const boardOf = (p: FeedParams) => JSON.stringify({ ...p, pipeline_state: undefined });

function stageFits(p: FeedParams, stage: PipelineState): boolean {
  if (p.pipeline_state?.length) return p.pipeline_state.includes(stage);
  return Boolean(p.include_excluded) || (ACTIVE_STAGES as readonly string[]).includes(stage);
}

const contains = (feed: Feed, id: string) => feed.pages.some((p) => p.items.some((i) => i.id === id));

/** The first page carries the exact total (the lane count). */
function withTotal(feed: Feed, pages: LocationPage[], by: number): Feed {
  const [first, ...rest] = pages;
  if (!first || by === 0 || first.page.total == null) return { ...feed, pages };
  return { ...feed, pages: [{ ...first, page: { ...first.page, total: Math.max(0, first.page.total + by) } }, ...rest] };
}

function dropItem(feed: Feed, id: string): Feed {
  if (!contains(feed, id)) return feed;
  return withTotal(
    feed,
    feed.pages.map((p) => ({ ...p, items: p.items.filter((i) => i.id !== id) })),
    -1,
  );
}

function replaceItem(feed: Feed, item: LocationSummary): Feed {
  return { ...feed, pages: feed.pages.map((p) => ({ ...p, items: p.items.map((i) => (i.id === item.id ? item : i)) })) };
}

function insertItem(feed: Feed, item: LocationSummary, pageIndex: number, index: number): Feed {
  if (contains(feed, item.id) || feed.pages.length === 0) return feed;
  const at = Math.min(pageIndex, feed.pages.length - 1);
  return withTotal(
    feed,
    feed.pages.map((p, i) => (i === at ? { ...p, items: [...p.items.slice(0, index), item, ...p.items.slice(index)] } : p)),
    1,
  );
}

/** Where a site sat in one loaded feed, so Undo puts it back in place. */
interface Placement {
  key: QueryKey;
  page: number;
  index: number;
  item: LocationSummary;
}

function removeFromFeeds(qc: QueryClient, orgId: string, id: string): Placement[] {
  const placements: Placement[] = [];
  for (const [key, feed] of qc.getQueriesData<Feed>({ queryKey: locationKeys.feeds(orgId) })) {
    if (!feed) continue;
    for (const [page, p] of feed.pages.entries()) {
      const index = p.items.findIndex((i) => i.id === id);
      const item = p.items[index];
      if (item) {
        placements.push({ key, page, index, item });
        break;
      }
    }
    qc.setQueryData<Feed>(key, dropItem(feed, id));
  }
  return placements;
}

function restoreToFeeds(qc: QueryClient, placements: Placement[], patch: Partial<LocationSummary> = {}) {
  for (const p of placements) qc.setQueryData<Feed>(p.key, (feed) => (feed ? insertItem(feed, { ...p.item, ...patch }, p.page, p.index) : feed));
}

/**
 * A confirmed stage move, applied to every loaded lane and list without refetching the pages
 * already scrolled through: out of lanes it no longer fits, onto the top of its new lane
 * (lanes sort by last update), updated in place elsewhere.
 */
function placeMoved(qc: QueryClient, orgId: string, updated: Location) {
  const feeds = qc.getQueriesData<Feed>({ queryKey: locationKeys.feeds(orgId) });
  let card: LocationSummary | undefined;
  const boards = new Set<string>();
  for (const [key, feed] of feeds) {
    const hit = feed?.pages.flatMap((p) => p.items).find((i) => i.id === updated.id);
    if (!hit) continue;
    card ??= hit;
    boards.add(boardOf(feedParams(key)));
  }
  if (!card) return;
  const moved: LocationSummary = { ...card, pipeline_state: updated.pipeline_state, version: updated.version, updated_at: updated.updated_at };
  for (const [key, feed] of feeds) {
    if (!feed) continue;
    const p = feedParams(key);
    const fits = stageFits(p, updated.pipeline_state);
    if (contains(feed, updated.id)) qc.setQueryData<Feed>(key, fits ? replaceItem(feed, moved) : dropItem(feed, updated.id));
    else if (fits && p.sort === '-updated_at' && boards.has(boardOf(p))) qc.setQueryData<Feed>(key, insertItem(feed, moved, 0, 0));
  }
}

/**
 * After a change to one site the loaded feeds are already right (see placeMoved and
 * removeFromFeeds): mark them stale without refetching every scrolled page now; refresh the rest.
 */
function refreshSite(qc: QueryClient, orgId: string, id: string) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: locationKeys.detail(orgId, id) }),
    qc.invalidateQueries({ queryKey: locationKeys.lists(orgId) }),
    qc.invalidateQueries({ queryKey: locationKeys.facets(orgId) }),
    qc.invalidateQueries({ queryKey: locationKeys.feeds(orgId), refetchType: 'none' }),
  ]);
}

/** After a failed command nothing local is trusted: refetch the site and the loaded feeds. */
function reloadSite(qc: QueryClient, orgId: string, id: string) {
  return Promise.all(siteChangedKeys(orgId, id).map((queryKey) => qc.invalidateQueries({ queryKey })));
}

/**
 * Stage moves from the board, the list and the location page. Plain moves go straight to
 * transitionLocation; the card moves only once the server confirmed. Guarded targets
 * (Ready, Disqualified, Cancelled, Archived) open the confirm dialog with a reason.
 */
export function useStageMove() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const toast = useToast();
  const [confirm, setConfirm] = useState<{ loc: LocationRef; to: PipelineState } | null>(null);
  const mutation = useMutation({
    mutationFn: ({ loc, to }: { loc: LocationRef; to: PipelineState }) =>
      unwrap(
        api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/transition', {
          params: {
            path: { orgId, locationId: loc.id },
            header: { 'If-Match': ifMatch(loc.version), 'Idempotency-Key': newIdempotencyKey() },
          },
          body: { target_state: to, reason: `Moved from ${stageLabel(loc.pipeline_state)} to ${stageLabel(to)}` },
        }),
      ),
    // The move is done once the server confirmed and the cards moved; the refresh runs on its own.
    onSuccess: (updated, { loc, to }) => {
      placeMoved(qc, orgId, updated);
      qc.setQueryData(locationKeys.detail(orgId, loc.id), updated);
      toast.show({ title: `Moved to ${stageLabel(to)}`, body: loc.name });
      void refreshSite(qc, orgId, loc.id);
    },
    onError: (e, { loc }) => {
      toast.show({ tone: 'accent', title: 'Not moved', body: `${loc.name} · ${failure(e)}`, durationMs: 10_000 });
      void reloadSite(qc, orgId, loc.id);
    },
  });
  const { mutate, isPending } = mutation;
  const request = useCallback(
    (loc: LocationRef, to: PipelineState) => {
      if (loc.pipeline_state === to || isPending) return;
      if (GUARDED_TARGETS.includes(to)) setConfirm({ loc, to });
      else mutate({ loc, to });
    },
    [isPending, mutate],
  );
  return {
    request,
    /** The location whose move is in flight, and where it is going. */
    pending: mutation.isPending ? mutation.variables : undefined,
    dialog: <TransitionDialog location={confirm?.loc ?? null} target={confirm?.to ?? null} onClose={() => setConfirm(null)} />,
  };
}

/**
 * Soft delete (deleteLocation) with Undo (restoreLocation). The site leaves every loaded lane and
 * list at once; a failure puts it back. Plain async rather than a mutation hook: the menu that
 * asked for it (a card, the side panel) may be gone before the server answers or Undo is pressed.
 */
export function useDeleteSite() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const toast = useToast();
  return useCallback(
    async (loc: LocationRef) => {
      const path = { orgId, locationId: loc.id };
      const placements = removeFromFeeds(qc, orgId, loc.id);
      const deleted = await unwrap(
        api.DELETE('/api/v1/orgs/{orgId}/locations/{locationId}', {
          params: { path, header: { 'If-Match': ifMatch(loc.version), 'Idempotency-Key': newIdempotencyKey() } },
          body: { reason: 'Deleted from the board' },
        }),
      ).catch((e: unknown) => {
        restoreToFeeds(qc, placements);
        toast.show({ tone: 'accent', title: 'Not deleted', body: `${loc.name} · ${failure(e)}`, durationMs: 10_000 });
        void reloadSite(qc, orgId, loc.id);
        return null;
      });
      if (!deleted) return;
      void refreshSite(qc, orgId, loc.id);
      const undo = async () => {
        try {
          const restored = await unwrap(
            api.POST('/api/v1/orgs/{orgId}/locations/{locationId}/restore', {
              params: { path, header: { 'If-Match': ifMatch(deleted.version), 'Idempotency-Key': newIdempotencyKey() } },
              body: { reason: 'Delete undone' },
            }),
          );
          restoreToFeeds(qc, placements, { version: restored.version, updated_at: restored.updated_at });
          qc.setQueryData(locationKeys.detail(orgId, loc.id), restored);
          toast.show({ title: 'Site restored', body: loc.name });
          void refreshSite(qc, orgId, loc.id);
        } catch (e) {
          toast.show({ tone: 'accent', title: 'Not restored', body: `${loc.name} · ${failure(e)}`, durationMs: 10_000 });
        }
      };
      toast.show({ title: 'Site deleted', body: loc.name, durationMs: 8000, action: { label: 'Undo', onAction: () => void undo() } });
    },
    [orgId, qc, toast],
  );
}
